import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'auth_token';

// Force every API request to use Bearer-token auth (no cookies). This works in every browser
// regardless of CORS / SameSite / third-party-cookie policies and avoids edge-proxy
// header collisions that previously broke login on Safari / Firefox strict mode.
axios.interceptors.request.use((config) => {
  if (config.url && config.url.includes(API_URL)) {
    // Disable cookies entirely — Authorization header is the only auth path
    config.withCredentials = false;
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) {
      config.headers = config.headers || {};
      if (!config.headers.Authorization) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
  }
  return config;
});

// If any 401 comes back, clear the stale token (so the next reload lands on /login cleanly)
axios.interceptors.response.use(
  (r) => r,
  (error) => {
    if (error?.response?.status === 401 && error.config?.url?.includes(API_URL)) {
      if (!error.config.url.includes('/api/auth/login')) {
        localStorage.removeItem(TOKEN_KEY);
      }
    }
    return Promise.reject(error);
  }
);

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // null = checking, false = not authenticated, object = authenticated
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setUser(false);
      setLoading(false);
      return;
    }
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`);
      setUser(response.data);
    } catch (error) {
      localStorage.removeItem(TOKEN_KEY);
      setUser(false);
    } finally {
      setLoading(false);
    }
  };

  const login = async (username, password) => {
    const response = await axios.post(`${API_URL}/api/auth/login`, { username, password });
    if (response.data?.access_token) {
      localStorage.setItem(TOKEN_KEY, response.data.access_token);
    }
    setUser(response.data);
    return response.data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`, {});
    } catch (error) {
      // ignore network errors on logout
    }
    localStorage.removeItem(TOKEN_KEY);
    setUser(false);
  };

  const value = {
    user,
    setUser,
    loading,
    login,
    logout,
    checkAuth
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
